import type { SalesDashboardMisFilter } from '@/composables/salesDashboardInjection'
import type { SdPerformanceScope } from '@/types/salesDashboardSession'
import type {
  SalesDashboardCustomRange,
  SalesDashboardFilters,
  SalesFilterOptions,
  SalesMode,
  SalesTimeRangePreset,
} from '@/types/salesDashboardShared'
import {
  SALES_DASHBOARD_EMPTY_CUSTOM_RANGE,
  SALES_DASHBOARD_EMPTY_FILTERS,
} from '@/types/salesDashboardShared'
import {
  canonicalizeRepKeys,
  cloneSalesDashboardFilters,
  sanitizeSalesStatusFilters,
} from '@/utils/salesDashboardSharedFilters'
import { sanitizeGlCodesForOptions } from '@/utils/salesDashboardStatusDefaults'

export const SALES_DASHBOARD_SESSION_STORAGE_KEY = 'rukh:sales-dashboard:shared-session'

const PERSISTENCE_VERSION = 1 as const

const VALID_TIME_RANGES = new Set<SalesTimeRangePreset>([
  'day',
  'week',
  'month',
  'ytd',
  'ttm',
  'custom',
])
const VALID_SALES_MODES = new Set<SalesMode>(['created', 'invoiced'])
const VALID_MIS = new Set<SalesDashboardMisFilter>(['both', 'cb', 'piq'])
const VALID_SCOPES = new Set<SdPerformanceScope>(['org', 'team'])

export interface PersistedSalesDashboardSession {
  version: typeof PERSISTENCE_VERSION
  timeRange: SalesTimeRangePreset
  customRange: SalesDashboardCustomRange
  selectedMonth: string
  salesMode: SalesMode
  performanceScope: SdPerformanceScope
  activeFilters: SalesDashboardFilters
}

export interface SalesDashboardAppliedSessionState {
  timeRange: SalesTimeRangePreset
  customRange: SalesDashboardCustomRange
  selectedMonth: string
  salesMode: SalesMode
  performanceScope: SdPerformanceScope
  activeFilters: SalesDashboardFilters
}

function isIsoMonthKey(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)
}

function isIsoDate(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
}

function parseStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.filter((entry): entry is string => typeof entry === 'string')
}

function parseCustomRange(value: unknown): SalesDashboardCustomRange {
  if (!value || typeof value !== 'object') {
    return { ...SALES_DASHBOARD_EMPTY_CUSTOM_RANGE }
  }
  const range = value as SalesDashboardCustomRange
  return {
    from: isIsoDate(range.from) ? range.from : '',
    to: isIsoDate(range.to) ? range.to : '',
  }
}

function parseFilters(value: unknown): SalesDashboardFilters {
  if (!value || typeof value !== 'object') {
    return cloneSalesDashboardFilters(SALES_DASHBOARD_EMPTY_FILTERS)
  }
  const filters = value as SalesDashboardFilters
  const mis = VALID_MIS.has(filters.mis) ? filters.mis : 'both'
  return {
    mis,
    teamUuids: parseStringArray(filters.teamUuids),
    teamMemberUuids: parseStringArray(filters.teamMemberUuids),
    repKeys: parseStringArray(filters.repKeys),
    orderStatuses: parseStringArray(filters.orderStatuses),
    productStatuses: parseStringArray(filters.productStatuses),
    invoiceStatuses: parseStringArray(filters.invoiceStatuses),
    glCodes: parseStringArray(filters.glCodes),
    customerStates: parseStringArray(filters.customerStates),
    accountGroups: parseStringArray(filters.accountGroups),
  }
}

export function loadPersistedSalesDashboardSession(): PersistedSalesDashboardSession | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(SALES_DASHBOARD_SESSION_STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw) as Partial<PersistedSalesDashboardSession>
    if (parsed.version !== PERSISTENCE_VERSION) return null
    if (!VALID_TIME_RANGES.has(parsed.timeRange as SalesTimeRangePreset)) return null
    if (!VALID_SALES_MODES.has(parsed.salesMode as SalesMode)) return null
    if (!VALID_SCOPES.has(parsed.performanceScope as SdPerformanceScope)) return null
    if (!isIsoMonthKey(parsed.selectedMonth)) return null

    return {
      version: PERSISTENCE_VERSION,
      timeRange: parsed.timeRange as SalesTimeRangePreset,
      customRange: parseCustomRange(parsed.customRange),
      selectedMonth: parsed.selectedMonth,
      salesMode: parsed.salesMode as SalesMode,
      performanceScope: parsed.performanceScope as SdPerformanceScope,
      activeFilters: parseFilters(parsed.activeFilters),
    }
  } catch {
    return null
  }
}

export function savePersistedSalesDashboardSession(
  state: SalesDashboardAppliedSessionState
): void {
  if (typeof window === 'undefined') return

  const payload: PersistedSalesDashboardSession = {
    version: PERSISTENCE_VERSION,
    timeRange: state.timeRange,
    customRange: { ...state.customRange },
    selectedMonth: state.selectedMonth,
    salesMode: state.salesMode,
    performanceScope: state.performanceScope,
    activeFilters: cloneSalesDashboardFilters(state.activeFilters),
  }

  window.localStorage.setItem(SALES_DASHBOARD_SESSION_STORAGE_KEY, JSON.stringify(payload))
}

export function clearPersistedSalesDashboardSession(): void {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(SALES_DASHBOARD_SESSION_STORAGE_KEY)
}

export function reconcilePersistedFiltersWithOptions(
  filters: SalesDashboardFilters,
  options: SalesFilterOptions
): SalesDashboardFilters {
  const statusOptions = {
    order: options.orderStatuses,
    product: options.productStatuses,
    invoice: options.invoiceStatuses,
  }
  const sanitizedStatus = sanitizeSalesStatusFilters(filters, statusOptions)
  const accountSet = new Set(options.accountGroups)
  const stateSet = new Set(options.customerStates)

  return {
    ...cloneSalesDashboardFilters(filters),
    ...sanitizedStatus,
    repKeys: canonicalizeRepKeys(filters.repKeys, options.salesReps),
    glCodes: sanitizeGlCodesForOptions(filters.glCodes, options.glCodes),
    customerStates: filters.customerStates.filter((state) => stateSet.has(state)),
    accountGroups: filters.accountGroups.filter((group) => accountSet.has(group)),
  }
}
